/** Evenly spaced timestamps for thumbnail frames, skipping a blank opening frame. */
export function videoFrameSampleTimes(
  duration: number,
  count = 4
): number[] {
  if (!Number.isFinite(duration) || duration <= 0 || count <= 0) {
    return [0];
  }
  const startPad = Math.min(0.15, duration * 0.08);
  const endPad = Math.min(0.08, duration * 0.05);
  const first = startPad;
  const last = Math.max(duration - endPad, first);
  if (count === 1 || last - first < 0.04) {
    return [first];
  }
  const times: number[] = [];
  for (let i = 0; i < count; i++) {
    const t = first + ((last - first) * i) / (count - 1);
    if (times.length === 0 || t - times[times.length - 1] >= 0.04) {
      times.push(t);
    }
  }
  return times;
}

function mountOffscreenVideo(videoElement: HTMLVideoElement): void {
  if (videoElement.parentNode) {
    return;
  }
  videoElement.muted = true;
  videoElement.playsInline = true;
  videoElement.setAttribute("playsinline", "");
  videoElement.style.position = "fixed";
  videoElement.style.left = "-10000px";
  videoElement.style.top = "0";
  videoElement.style.width = "48px";
  videoElement.style.height = "48px";
  videoElement.style.opacity = "0";
  videoElement.style.pointerEvents = "none";
  document.body.appendChild(videoElement);
}

/** Blink Chromium (not WebKit Chrome iOS / Safari): WebM seeking snaps to keyframes. */
export function usesPlaybackFrameCapture(
  userAgent =
    typeof navigator !== "undefined" ? navigator.userAgent : ""
): boolean {
  if (/crios|fxios|iphone|ipad|ipod/i.test(userAgent)) {
    return false;
  }
  return /chrome|chromium|edg|opr/i.test(userAgent);
}

function waitForPresentedFrame(
  videoElement: HTMLVideoElement,
  timeoutMs = 2500
): Promise<number> {
  const withFrameCallback = videoElement as HTMLVideoElement & {
    requestVideoFrameCallback?: (
      callback: (now: number, metadata: { mediaTime: number }) => void
    ) => number;
  };
  return new Promise(resolve => {
    let settled = false;
    const finish = (mediaTime: number) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(mediaTime);
    };
    const timeoutId = setTimeout(() => {
      finish(videoElement.currentTime);
    }, timeoutMs);
    if (typeof withFrameCallback.requestVideoFrameCallback === "function") {
      withFrameCallback.requestVideoFrameCallback((_now, metadata) => {
        clearTimeout(timeoutId);
        finish(metadata.mediaTime);
      });
      return;
    }
    const onTime = () => {
      videoElement.removeEventListener("timeupdate", onTime);
      clearTimeout(timeoutId);
      finish(videoElement.currentTime);
    };
    videoElement.addEventListener("timeupdate", onTime);
  });
}

async function captureFramesBySeek(
  videoElement: HTMLVideoElement,
  times: number[]
): Promise<Blob[]> {
  const imagesBlob: Blob[] = [];
  try {
    await ensureVideoReady(videoElement);
    for (const second of times) {
      await seekToSecond(videoElement, second);
      const blob = await generateFrame(videoElement);
      imagesBlob.push(blob);
    }
  } catch (error) {
    console.error("Failed to capture frame: " + error);
  }
  return imagesBlob;
}

async function captureFramesByPlayback(
  videoElement: HTMLVideoElement,
  times: number[]
): Promise<Blob[]> {
  const imagesBlob: Blob[] = [];
  const duration = Number.isFinite(videoElement.duration)
    ? videoElement.duration
    : 0;
  mountOffscreenVideo(videoElement);
  videoElement.muted = true;
  videoElement.playsInline = true;
  videoElement.playbackRate =
    duration > 0 ? Math.min(16, Math.max(4, duration / 6)) : 1;

  const grab = async () => {
    await ensureVideoReady(videoElement);
    const blob = await generateFrame(videoElement);
    imagesBlob.push(blob);
  };

  try {
    let index = 0;
    await videoElement.play();
    const deadline =
      Date.now() + Math.min(20000, Math.max(8000, duration * 250 + 2000));
    while (
      index < times.length &&
      Date.now() < deadline &&
      !videoElement.ended
    ) {
      const mediaTime = await waitForPresentedFrame(videoElement);
      if (mediaTime + 0.03 >= times[index] || videoElement.ended) {
        videoElement.pause();
        await grab();
        index += 1;
        if (index < times.length && !videoElement.ended) {
          await videoElement.play();
        }
      }
    }
  } catch (error) {
    console.error("Failed to capture frame: " + error);
  }
  return imagesBlob;
}

export const captureFrames = async (
  videoElement: HTMLVideoElement
): Promise<Blob[]> => {
  const duration = Number.isFinite(videoElement.duration)
    ? videoElement.duration
    : 0;
  const times = videoFrameSampleTimes(duration, 4);
  const imagesBlob = usesPlaybackFrameCapture()
    ? await captureFramesByPlayback(videoElement, times)
    : await captureFramesBySeek(videoElement, times);
  cleanUp(videoElement);
  return imagesBlob;
};

export const seekToSecond = (
  videoElement: HTMLVideoElement,
  second: number,
  maxRetries = 3,
  retryDelay = 1000,
  seekTimeoutMs = 5000
): Promise<void> => {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    function attemptSeek() {
      attempts++;
      let seekTimeoutId: ReturnType<typeof setTimeout> | null = null;

      function cleanup(onSeeked: () => void) {
        if (seekTimeoutId != null) {
          clearTimeout(seekTimeoutId);
        }
        videoElement.removeEventListener("seeked", onSeeked);
      }

      function onSeeked() {
        cleanup(onSeeked);
        resolve();
      }

      videoElement.addEventListener("seeked", onSeeked);

      seekTimeoutId = setTimeout(() => {
        cleanup(onSeeked);
        if (attempts < maxRetries) {
          console.warn(
            `Seek to ${second}s timed out (attempt ${attempts}), retrying in ${retryDelay}ms...`
          );
          setTimeout(attemptSeek, retryDelay);
        } else {
          reject(
            new Error(
              `Failed to seek to second ${second} after ${maxRetries} attempts (timeout)`
            )
          );
        }
      }, seekTimeoutMs);

      videoElement.onerror = () => {
        cleanup(onSeeked);
        if (attempts < maxRetries) {
          console.warn(
            `Seek attempt ${attempts} failed, retrying in ${retryDelay}ms...`
          );
          setTimeout(attemptSeek, retryDelay);
        } else {
          reject(
            new Error(
              `Failed to seek to second ${second} after ${maxRetries} attempts`
            )
          );
        }
      };

      videoElement.currentTime = second;
    }

    attemptSeek();
  });
};

export const ensureVideoReady = (
  videoElement: HTMLVideoElement,
  maxWaitTime = 10000,
  checkInterval = 100
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    function checkReadyState() {
      if (videoElement.readyState >= 2) {
        resolve();
      } else if (Date.now() - startTime > maxWaitTime) {
        reject(
          new Error(
            "Video not ready for frame capture within maximum wait time"
          )
        );
      } else {
        setTimeout(checkReadyState, checkInterval);
      }
    }

    checkReadyState();
  });
};

export const generateFrame = (
  videoElement: HTMLVideoElement
): Promise<Blob | null> => {
  return new Promise((resolve, reject) => {
    try {
      let canvas = document.createElement("canvas");
      const maxWidth = 1000;
      const videoSize = Math.max(
        videoElement.videoWidth,
        videoElement.videoHeight
      );

      // Resize canvas to preserve aspect ratio
      canvas.height = Math.min(maxWidth, videoSize);
      canvas.width = canvas.height;

      const context = canvas.getContext("2d");
      const finalWidth = (videoElement.videoWidth / videoSize) * canvas.width;
      const finalHeight =
        (videoElement.videoHeight / videoSize) * canvas.height;

      context.drawImage(
        videoElement,
        0,
        0,
        videoElement.videoWidth,
        videoElement.videoHeight,
        (canvas.width - finalWidth) / 2,
        (canvas.height - finalHeight) / 2,
        finalWidth,
        finalHeight
      );

      // Handle orientation issues by checking bottom-right corner
      let bottomRightImageData = context.getImageData(
        (canvas.width - finalWidth) / 2 + finalWidth - 2,
        (canvas.height - finalHeight) / 2 + finalHeight - 2,
        2,
        2
      );

      let allZero = true;
      bottomRightImageData.data.forEach(
        val => (allZero = allZero && val === 0)
      );
      context.fillStyle = "white";
      context.fillRect(0, 0, canvas.width, canvas.height);
      let frameWidth = videoElement.videoWidth;
      let frameHeight = videoElement.videoHeight;

      if (allZero) {
        frameWidth = videoElement.videoHeight;
        frameHeight = videoElement.videoWidth;
      }

      // Draw the corrected image
      context.drawImage(
        videoElement,
        0,
        0,
        frameWidth,
        frameHeight,
        (canvas.width - finalWidth) / 2,
        (canvas.height - finalHeight) / 2,
        finalWidth,
        finalHeight
      );

      canvas.toBlob(
        blob => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to create Blob from canvas"));
          }
          context.clearRect(0, 0, canvas.width, canvas.height);
          canvas.remove();
          canvas = null;
        },
        "image/jpeg",
        1.0
      );
    } catch (error) {
      reject(new Error("Failed to generate frame: " + error));
    }
  });
};

export const cleanUp = (videoElement: HTMLVideoElement): void => {
  videoElement.pause();
  if (videoElement.parentNode) {
    videoElement.parentNode.removeChild(videoElement);
  }
  videoElement.removeAttribute("src");
  videoElement.load();
};
